import { useEffect, useRef } from "react";
import { Button, Typography } from "antd";
import { connect, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import * as actions from "./actions";
import { getConfirmedAppointments } from "./reducers";
import ContentContainer from "../../components/ContentContainer";
import FullNameWithTitleLabel from "../../components/FullNameWithTitleLabel";
import { trackEvent, trackButtonClick, resetSession, getSessionContext } from "utils/analytics";
import usePageView from "../../hooks/usePageView";
import {
	CheckCircleFilled,
	CalendarOutlined,
	ClockCircleOutlined,
	UserOutlined,
	EnvironmentOutlined,
	PhoneOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const Complete = ({ confirmedAppointments }) => {
	const confettiTriggered = useRef(false);

	const currentLocation = useSelector(
		(state) => state.locations.currentLocation,
	);
	const entryUrl = useSelector(
		(state) => state.session.entryUrl,
	);

	const { name: locationName, phone, themeColor } = currentLocation || {};

	// Track page view with step info
	usePageView("Booking Confirmed!", 6, "complete", 100);

	// Fire confetti on mount
	useEffect(() => {
		if (confettiTriggered.current) return;
		confettiTriggered.current = true;

		const fireConfetti = () => {
			const colors = [themeColor || '#52c41a', '#1890ff', '#722ed1', '#faad14', '#eb2f96'];

			// Center burst
			confetti({
				particleCount: 100,
				spread: 70,
				origin: { y: 0.6 },
				colors,
			});

			// Left side
			setTimeout(() => {
				confetti({
					particleCount: 50,
					angle: 60,
					spread: 55,
					origin: { x: 0 },
					colors,
				});
			}, 150);

			// Right side
			setTimeout(() => {
				confetti({
					particleCount: 50,
					angle: 120,
					spread: 55,
					origin: { x: 1 },
					colors,
				});
			}, 300);
		};

		// Small delay to let page render first
		setTimeout(fireConfetti, 300);
	}, [themeColor]);

	// Track booking completed
	useEffect(() => {
		const session = getSessionContext();
		trackEvent("booking_completed", {
			total_session_duration_seconds: session.sessionStartTime
				? Math.round((Date.now() - session.sessionStartTime) / 1000)
				: null,
			appointments_count: confirmedAppointments?.length || 0,
		});
	}, [confirmedAppointments]);

	const handleBookAnother = () => {
		trackEvent("book_another_clicked");
		trackButtonClick('book_another_button');
		resetSession();
	};

	// Get first appointment for display
	const firstAppointment = confirmedAppointments?.[0];

	return (
		<ContentContainer
			step="6"
			stepPercentage="100"
			showNext={false}
			pageTitle="Booking Confirmed"
			themeColor={themeColor}
		>
			<div style={{ maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
				{/* Success Header */}
				<div style={{
					textAlign: "center",
					marginBottom: 32,
					paddingTop: 8,
				}}>
					<div style={{
						width: 72,
						height: 72,
						borderRadius: "50%",
						backgroundColor: `${themeColor || '#52c41a'}15`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						margin: "0 auto 16px",
					}}>
						<CheckCircleFilled style={{
							fontSize: 40,
							color: themeColor || '#52c41a',
						}} />
					</div>
					<Title level={3} style={{ marginBottom: 8 }}>
						You're all booked!
					</Title>
					<Text type="secondary">
						A confirmation has been sent to your email and phone
					</Text>
				</div>

				{/* Booking Summary Card */}
				{firstAppointment && (
					<div style={{
						background: "#fff",
						borderRadius: 12,
						border: "1px solid #f0f0f0",
						overflow: "hidden",
						marginBottom: 16,
					}}>
						{/* Card Header */}
						<div style={{
							padding: "16px 20px",
							borderBottom: "1px solid #f0f0f0",
							backgroundColor: "#fafafa",
						}}>
							<Text strong style={{ fontSize: 15 }}>Appointment Details</Text>
						</div>

						{/* Appointment(s) */}
						{confirmedAppointments?.map((appointment, index) => (
							<div
								key={appointment.id || index}
								style={{
									padding: "16px 20px",
									borderBottom: index < confirmedAppointments.length - 1 ? "1px solid #f5f5f5" : "none",
								}}
							>
								{/* Service Name */}
								<Text strong style={{
									fontSize: 16,
									display: "block",
									marginBottom: 12,
									color: themeColor || '#1890ff',
								}}>
									{appointment.appointmentReason?.name || appointment.reason?.name || "Appointment"}
								</Text>

								{/* Details */}
								<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
									{/* Date */}
									<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
										<CalendarOutlined style={{ color: "#999", fontSize: 15 }} />
										<Text style={{ fontSize: 14 }}>
											{appointment.date || appointment.appointmentDate}
										</Text>
									</div>

									{/* Time */}
									<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
										<ClockCircleOutlined style={{ color: "#999", fontSize: 15 }} />
										<Text style={{ fontSize: 14 }}>
											{appointment.startTime || appointment.time}
											{(appointment.appointmentReason?.minutes || appointment.duration) && (
												<Text type="secondary" style={{ marginLeft: 4 }}>
													({appointment.appointmentReason?.minutes || appointment.duration} mins)
												</Text>
											)}
										</Text>
									</div>

									{/* Practitioner */}
									{(appointment.practitioner || appointment.practitionerName) && (
										<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
											<UserOutlined style={{ color: "#999", fontSize: 15 }} />
											<Text style={{ fontSize: 14 }}>
												{appointment.practitioner?.fullNameWithTitle ? (
													<FullNameWithTitleLabel name={appointment.practitioner.fullNameWithTitle} />
												) : (
													appointment.practitionerName || appointment.practitioner?.name
												)}
											</Text>
										</div>
									)}
								</div>
							</div>
						))}

						{/* Location Info */}
						<div style={{
							padding: "16px 20px",
							backgroundColor: "#fafafa",
							borderTop: "1px solid #f0f0f0",
						}}>
							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
									<EnvironmentOutlined style={{ color: "#999", fontSize: 15 }} />
									<Text style={{ fontSize: 14 }}>{locationName}</Text>
								</div>
								{phone && (
									<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
										<PhoneOutlined style={{ color: "#999", fontSize: 15 }} />
										<Text style={{ fontSize: 14 }}>{phone}</Text>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Info Message */}
				<div style={{
					textAlign: "center",
					padding: "12px 16px",
					background: "#f6ffed",
					borderRadius: 8,
					marginBottom: 16,
					border: "1px solid #b7eb8f",
				}}>
					<Text style={{ fontSize: 13, color: "#389e0d" }}>
						Please contact us if you need to move or cancel your appointment
					</Text>
				</div>

				{/* Book Another Button */}
				<Link to={entryUrl}>
					<Button
						block
						size="large"
						style={{
							borderRadius: 8,
							fontWeight: 500,
							height: 48,
							borderColor: themeColor,
							color: themeColor,
						}}
						onClick={handleBookAnother}
					>
						Book another appointment
					</Button>
				</Link>

				{/* Add to Calendar hint */}
				<Text
					type="secondary"
					style={{
						display: "block",
						textAlign: "center",
						marginTop: 16,
						fontSize: 13,
					}}
				>
					We may send you additional information via SMS or email
				</Text>
			</div>
		</ContentContainer>
	);
};

const mapStateToProps = (state) => ({
	confirmedAppointments: getConfirmedAppointments(state.booking),
});

export default connect(mapStateToProps, actions)(Complete);
